import {forwardRef} from 'react';

import Link from '@mui/material/Link';
import {useTheme} from '@mui/material/styles';
import Box, {BoxProps} from '@mui/material/Box';
import {BASE_ROUTE} from 'src/routes/BaseRoute';
import {PRIVATE_ROUTES} from 'src/routes/RouteConstants';
import {RouterLink} from 'src/routes/components';

// ----------------------------------------------------------------------

export interface LogoProps extends BoxProps {
  disabledLink?: boolean;
  applicationName?: string;
}

const Logo = forwardRef<HTMLDivElement, LogoProps>(
  ({disabledLink = false, applicationName, sx, ...other}, ref) => {
    const theme = useTheme();

    const PRIMARY_MAIN = theme.palette.primary.main;

    // Define logo source & size based on applicationName
    let logoSrc = '/logos/apps/bytepaddemo-hover.svg';
    let logoSize = {width: 40, height: 40};

    if (applicationName === 'Bytepad_Legacy') {
      logoSrc = '/logos/apps/bytepaddemo-hover.svg';
      logoSize = {width: 40, height: 40};
    } else if (applicationName === 'LMS') {
      logoSrc = '/logos/apps/bytepadlmslogo.svg';
      logoSize = {width: 50, height: 50};
    } else if (applicationName === 'App_Rationalization') {
      logoSrc = '/logos/apps/bytepadappharbor-hover.svg';
      logoSize = {width: 40, height: 40};
    }

    const logo = (
      <Box
        ref={ref}
        component="div"
        sx={{
          width: logoSize.width,
          height: logoSize.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
        {...other}>
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
          />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={35}
            height={30}
            viewBox="793.834 561.689 35 30">
            <defs>
              <style>
                {
                  '.aJ-mlzyO1eFfillColor{fill:#4d4d4d;fill-opacity:1}.aJ-mlzyO1eFstrokeColor-str{stroke:#313131;stroke-opacity:1}.companyName{fill:#4d4d4d;fill-opacity:1}.icon3-str{stroke:#ffae78;stroke-opacity:1}'
                }
              </style>
            </defs>
            <svg
              width={35}
              height={30}
              x={793.834}
              y={561.689}
              preserveAspectRatio="none"
              viewBox="0 4.167 100 91.667">
              <path
                d="M87.5 12.5h-25c-4.167 0-4.167 0-4.167 4.167v16.667c0 4.167 0 4.167 4.167 4.167h25c4.167 0 4.167 0 4.167-4.167V16.667c0-4.167 0-4.167-4.167-4.167zM45.833 4.167H29.167C25 4.167 25 4.167 25 8.333v8.333c0 4.167 0 4.167 4.167 4.167h16.667c4.167 0 4.167 0 4.167-4.167V8.333C50 4.167 50 4.167 45.833 4.167zm50 41.666H62.5c-4.167 0-4.167 0-4.167 4.167v33.333c0 4.167 0 4.167 4.167 4.167h33.333c4.167 0 4.167 0 4.167-4.167V50c0-4.167 0-4.167-4.167-4.167zm-50 25h-25c-4.167 0-4.167 0-4.167 4.167v16.667c0 4.167 0 4.167 4.167 4.167h25c4.167 0 4.167 0 4.167-4.167V75c0-4.167 0-4.167-4.167-4.167zm0-41.666H4.167C0 29.167 0 29.167 0 33.333v25C0 62.5 0 62.5 4.167 62.5h41.667C50 62.5 50 62.5 50 58.333v-25c0-4.166 0-4.166-4.167-4.166z"
                style={{
                  fill: PRIMARY_MAIN,
                  fillOpacity: 1
                }}
              />
            </svg>
          </svg>
        )}
      </Box>
    );

    if (disabledLink) {
      return logo;
    }

    return (
      <Link
        component={RouterLink}
        href={`/${BASE_ROUTE}/${PRIVATE_ROUTES.DASHBOARD}`}
        sx={{display: 'contents'}}>
        {logo}
      </Link>
    );
  }
);

export default Logo;
